/**
 * SheetPlannerRow - Single row in the spreadsheet planner
 * 
 * EF-SHEET-PLANNER v1.2: Clickable entities + resizable columns
 * EF-SHEET-PLANNER-CREW-CELL-INTERACTION v1.0: Crew cell click-to-expand + context menu
 * 
 * Navigation Contract:
 * - Project/Shift/Venue/Location names are clickable
 * - Navigate to their respective edit/view pages
 * - Uses same patterns as SHIFT Crew Planner
 * 
 * Explicit Save Model:
 * - Edits stay in local state until Save button clicked
 * - Crew assignments are immediate (same as existing planner)
 */
import { useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../ui/context-menu";
import {
  ChevronRight,
  ChevronDown,
  Users,
  AlertTriangle,
  Save,
  X,
  UserPlus,
  Mail,
  Bell,
  Clipboard,
  ClipboardPaste,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { SheetPlannerExpandedSection } from "./SheetPlannerExpandedSection";
import ShiftRuleStatusBadge from "../shifts/ShiftRuleStatusBadge";

export function SheetPlannerRow({
  shift,
  project,
  projectName,
  venue,
  venueName,
  location,
  locationName,
  locationAddress,
  crew,
  lineItems,
  venueLocations,
  isExpanded,
  onToggle,
  localEdits,
  onLocalEdit,
  onSave,
  onCancelEdit,
  onAddCrew,
  onRemoveCrew,
  onToggleCrewChief,
  onToggleConfirmed,
  onToggleWorked,
  onSendPWALink,
  onCopyPWAInstall,
  onEmailCrew,
  onPushCrew,
  onCopyCrew,
  onPasteCrew,
  hasClipboard,
  onNavigateProject,
  onNavigateShift,
  onNavigateVenue,
  onNavigateLocation,
  onEditCrew,
  columnWidths,
  scale,
  formatCurrency,
}) {
  // Droppable for crew assignment
  const { setNodeRef, isOver } = useDroppable({
    id: `shift-drop-${shift.id}`,
    data: { type: "shift-drop", shiftId: shift.id },
  });

  // Calculate crew status
  const staffRequired = shift.staff_required || 0;
  const assignedCount = crew.length;
  const crewStatus = useMemo(() => {
    if (assignedCount < staffRequired) return "under";
    if (assignedCount > staffRequired) return "over";
    return "ok";
  }, [assignedCount, staffRequired]);

  // Format date for display (compact)
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  // Format single time value
  const formatTime = (timeStr) => {
    if (!timeStr) return "—";
    return timeStr;
  };

  // Format time range for tooltip
  // (Reserved for future use)

  // Check if row has unsaved changes
  const hasChanges = localEdits._dirty;

  // Get display values (local edits override shift data)
  const displayStartTime = localEdits.start_time ?? shift.start_time ?? "—";
  const displayEndTime = localEdits.end_time ?? shift.end_time ?? "—";
  const displayHours = shift.duration_hours?.toFixed(1) ?? "—";

  // Calculate shift subtotal from line items
  const shiftSubtotal = useMemo(() => {
    return (lineItems || []).reduce((sum, item) => {
      const qty = item.quantity || 0;
      const rate = item.unit_rate || 0;
      return sum + (qty * rate);
    }, 0);
  }, [lineItems]);

  // Row height based on zoom
  const rowHeight = Math.round(22 * scale);
  const fontSize = Math.round(11 * scale);
  const smallFontSize = Math.round(9 * scale);

  // Clickable cell handler - stops propagation to prevent row toggle
  const handleCellClick = (e, callback) => {
    e.stopPropagation();
    callback();
  };

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "border-b border-border/30 transition-colors",
        isOver && "bg-primary/5",
        hasChanges && "bg-amber-500/5"
      )}
    >
      {/* Main Row - Fixed height, single line */}
      <div
        className="flex items-center cursor-pointer hover:bg-surface-elevated/30"
        style={{ height: `${rowHeight}px`, fontSize: `${fontSize}px` }}
        onClick={onToggle}
        data-testid={`sheet-row-${shift.id}`}
      >
        {/* EF-SHEET-UX-POLISH-PASS-01: Project - Plain clickable text (no hover) */}
        <div 
          className="px-1 border-r border-border/20 truncate shrink-0 hover:text-primary hover:underline cursor-pointer"
          style={{ width: `${columnWidths.project}px` }}
          onClick={(e) => handleCellClick(e, onNavigateProject)}
        >
          {projectName}
        </div>

        {/* EF-SHEET-UX-POLISH-PASS-01: Shift Name - Enhanced hover with full operational info */}
        <HoverCard openDelay={300} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div 
              className="px-1 border-r border-border/20 truncate flex items-center gap-0.5 shrink-0 hover:text-primary cursor-pointer"
              style={{ width: `${columnWidths.shift}px` }}
              onClick={(e) => handleCellClick(e, onNavigateShift)}
            >
              {isExpanded ? (
                <ChevronDown style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} className="shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight style={{ width: `${10 * scale}px`, height: `${10 * scale}px` }} className="shrink-0 text-muted-foreground" />
              )}
              <span className="truncate hover:underline">
                {shift.name}
              </span>
              {/* EVENTSFLO-SHIFT-RULE-VISIBILITY-AND-CREW-PLANNER-ENFORCEMENT-AUDIT-V1 */}
              <ShiftRuleStatusBadge shiftId={shift.id} compact />
            </div>
          </HoverCardTrigger>
          <HoverCardContent side="right" align="start" className="p-0 w-80" style={{ fontSize: `${smallFontSize}px` }}>
            {/* Header - Shift name with accent bar */}
            <div className="px-3 py-2 border-b border-l-4 border-l-primary/40 bg-primary/5 font-medium">
              {shift.name}
            </div>
            {/* Body - Condensed version of expanded view */}
            <div className="px-3 py-2 space-y-1 text-xs">
              {/* Row 1: Project & Date */}
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground truncate">{projectName}</span>
                <span className="shrink-0">{formatDate(shift.date)}</span>
              </div>
              
              {/* Row 2: Time & Duration */}
              <div className="flex justify-between">
                <span>{formatTime(shift.start_time)} – {formatTime(shift.end_time)}</span>
                <span className="font-medium">{shift.duration_hours?.toFixed(1) || 0}h</span>
              </div>
              
              {/* Row 3: Venue & Location */}
              <div className="text-muted-foreground truncate">
                {venueName}{locationName ? ` / ${locationName}` : ""}
                {shift.out_of_town && <span className="text-blue-500 ml-1">(OOT)</span>}
              </div>
              
              {/* Row 4: Staffing */}
              <div className="flex justify-between pt-1 border-t border-border/30">
                <span className="text-muted-foreground">Staffing:</span>
                <span className={cn(
                  crew.length < (shift.staff_required || 1) && "text-amber-500",
                  crew.length > (shift.staff_required || 1) && "text-red-500",
                  crew.length === (shift.staff_required || 1) && "text-green-500"
                )}>
                  {crew.length}/{shift.staff_required || 1} assigned
                </span>
              </div>
              
              {/* Row 5: Cost Summary (if lineItems available) */}
              {lineItems && lineItems.length > 0 && (() => {
                const items = Array.isArray(lineItems) ? lineItems : [];
                const primaryItems = items.filter(item => item.type_snapshot === 'primary' || item.is_primary);
                const supplementItems = items.filter(item => item.type_snapshot !== 'primary' && !item.is_primary);
                
                // Extract meal items for display
                const mealItems = supplementItems.filter(item => 
                  item.name_snapshot?.toLowerCase().includes('meal') ||
                  item.supplement_group_snapshot?.toLowerCase().includes('meal')
                );
                
                const primaryTotal = primaryItems.reduce((acc, item) => {
                  const qty = item.quantity || 1;
                  const rate = item.unit_rate || 0;
                  const duration = item.duration_hours || shift.duration_hours || 0;
                  return acc + (qty * rate * duration);
                }, 0);
                
                const supplementTotal = supplementItems.reduce((acc, item) => acc + (item.computed_total || 0), 0);
                const shiftTotal = primaryTotal + supplementTotal;
                
                return (
                  <div className="pt-1 border-t border-border/30 space-y-0.5">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Primary Crew:</span>
                      <span>{formatCurrency(primaryTotal)}</span>
                    </div>
                    {/* Crew Meals breakdown */}
                    {mealItems.length > 0 && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Crew Meals ({mealItems.reduce((acc, m) => acc + (m.quantity || 1), 0)}):</span>
                        <span>{formatCurrency(mealItems.reduce((acc, m) => acc + (m.computed_total || 0), 0))}</span>
                      </div>
                    )}
                    {supplementTotal > 0 && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Associated Costs:</span>
                        <span>{formatCurrency(supplementTotal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium pt-0.5">
                      <span>TOTAL:</span>
                      <span className="text-primary">{formatCurrency(shiftTotal)}</span>
                    </div>
                  </div>
                );
              })()}
              
              {/* Notes preview */}
              {shift.notes && (
                <div className="pt-1 border-t border-border/30">
                  <p className="text-muted-foreground line-clamp-2">{shift.notes}</p>
                </div>
              )}
            </div>
          </HoverCardContent>
        </HoverCard>

        {/* Venue - Clickable */}
        <div 
          className="px-1 border-r border-border/20 truncate shrink-0 hover:text-primary hover:underline cursor-pointer"
          style={{ width: `${columnWidths.venue}px` }}
          onClick={(e) => handleCellClick(e, onNavigateVenue)}
          title={`Click to open ${venueName}`}
        >
          {venueName}
        </div>

        {/* Location - Clickable */}
        <div 
          className="px-1 border-r border-border/20 truncate shrink-0 hover:text-primary hover:underline cursor-pointer"
          style={{ width: `${columnWidths.location}px` }}
          onClick={(e) => handleCellClick(e, onNavigateLocation)}
          title={`Click to open ${locationName}`}
        >
          {locationName}
        </div>

        {/* Date */}
        <div 
          className="px-1 border-r border-border/20 text-center truncate shrink-0"
          style={{ width: `${columnWidths.date}px`, fontSize: `${smallFontSize}px` }}
        >
          {formatDate(shift.date)}
        </div>

        {/* Start Time */}
        <div 
          className="px-1 border-r border-border/20 text-center shrink-0"
          style={{ width: `${columnWidths.start}px`, fontSize: `${smallFontSize}px` }}
        >
          {displayStartTime}
        </div>

        {/* End Time */}
        <div 
          className="px-1 border-r border-border/20 text-center shrink-0"
          style={{ width: `${columnWidths.end}px`, fontSize: `${smallFontSize}px` }}
        >
          {displayEndTime}
        </div>

        {/* Hours */}
        <div 
          className="px-1 border-r border-border/20 text-center shrink-0"
          style={{ width: `${columnWidths.hours}px`, fontSize: `${smallFontSize}px` }}
        >
          {displayHours}
        </div>

        {/* Crew Status - Click to expand, right-click for context menu */}
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div 
              className="px-1 border-r border-border/20 flex items-center justify-center shrink-0 cursor-pointer hover:bg-surface-elevated/50"
              style={{ width: `${columnWidths.crew}px` }}
              onClick={(e) => { 
                e.stopPropagation(); 
                onToggle(); // Click to expand (same as row click)
              }}
              data-testid={`crew-cell-${shift.id}`}
            >
              <Badge
                className={cn(
                  "px-1 py-0",
                  crewStatus === "under" && "bg-amber-500/20 text-amber-600 border-amber-500/30",
                  crewStatus === "over" && "bg-red-500/20 text-red-600 border-red-500/30",
                  crewStatus === "ok" && "bg-green-500/20 text-green-600 border-green-500/30"
                )}
                style={{ fontSize: `${8 * scale}px`, height: `${14 * scale}px` }}
              >
                <Users style={{ width: `${8 * scale}px`, height: `${8 * scale}px`, marginRight: `${2 * scale}px` }} />
                {assignedCount}/{staffRequired}
                {crewStatus !== "ok" && <AlertTriangle style={{ width: `${8 * scale}px`, height: `${8 * scale}px`, marginLeft: `${2 * scale}px` }} />}
              </Badge>
              
              {/* Save button appears when dirty */}
              {hasChanges && (
                <div className="flex items-center gap-0.5 ml-1">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onSave(); }}
                    className="px-1"
                    style={{ height: `${14 * scale}px`, fontSize: `${7 * scale}px` }}
                    data-testid={`save-shift-${shift.id}`}
                  >
                    <Save style={{ width: `${8 * scale}px`, height: `${8 * scale}px` }} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onCancelEdit(); }}
                    className="px-0.5"
                    style={{ height: `${14 * scale}px`, width: `${14 * scale}px` }}
                  >
                    <X style={{ width: `${8 * scale}px`, height: `${8 * scale}px` }} />
                  </Button>
                </div>
              )}
            </div>
          </ContextMenuTrigger>
          {/* Context menu - matches SHIFT Crew Planner actions exactly */}
          <ContextMenuContent className="w-52" style={{ fontSize: `${smallFontSize}px` }}>
            <ContextMenuItem 
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              style={{ height: `${24 * scale}px` }}
            >
              <UserPlus style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${6 * scale}px` }} />
              {isExpanded ? "Collapse Panel" : "Expand to Assign Crew"}
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem 
              onClick={(e) => { e.stopPropagation(); /* Email action - same as SHIFT Planner */ }}
              style={{ height: `${24 * scale}px` }}
              disabled={assignedCount === 0}
            >
              <Mail style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${6 * scale}px` }} />
              Email Crew
            </ContextMenuItem>
            <ContextMenuItem 
              onClick={(e) => { e.stopPropagation(); /* App Alert - same as SHIFT Planner */ }}
              style={{ height: `${24 * scale}px` }}
              disabled={assignedCount === 0}
            >
              <Bell style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${6 * scale}px` }} />
              App Alert
            </ContextMenuItem>
            <ContextMenuSeparator />
            {/* Copy/Paste Crew - same handlers as SHIFT Planner */}
            <ContextMenuItem 
              onClick={(e) => { e.stopPropagation(); onCopyCrew(); }}
              style={{ height: `${24 * scale}px` }}
              disabled={assignedCount === 0}
            >
              <Clipboard style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${6 * scale}px` }} />
              Copy Crew ({assignedCount})
            </ContextMenuItem>
            <ContextMenuItem 
              onClick={(e) => { e.stopPropagation(); onPasteCrew(); }}
              style={{ height: `${24 * scale}px` }}
              disabled={!hasClipboard}
            >
              <ClipboardPaste style={{ width: `${12 * scale}px`, height: `${12 * scale}px`, marginRight: `${6 * scale}px` }} />
              Paste Crew
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        {/* Address - Far right */}
        <div 
          className="px-1 truncate shrink-0 text-muted-foreground"
          style={{ width: `${columnWidths.address}px`, fontSize: `${smallFontSize}px` }}
          title={locationAddress}
        >
          {locationAddress}
        </div>
      </div>

      {/* Expanded Section */}
      {isExpanded && (
        <SheetPlannerExpandedSection
          shift={shift}
          projectName={projectName}
          venueName={venueName}
          locationName={locationName}
          crew={crew}
          lineItems={lineItems}
          venueLocations={venueLocations}
          localEdits={localEdits}
          onLocalEdit={onLocalEdit}
          onSave={onSave}
          onCancelEdit={onCancelEdit}
          onAddCrew={onAddCrew}
          onRemoveCrew={onRemoveCrew}
          onToggleCrewChief={onToggleCrewChief}
          onToggleConfirmed={onToggleConfirmed}
          onToggleWorked={onToggleWorked}
          onSendPWALink={onSendPWALink}
          onCopyPWAInstall={onCopyPWAInstall}
          onEmailCrew={onEmailCrew}
          onPushCrew={onPushCrew}
          onEditCrew={onEditCrew}
          onCopyCrew={onCopyCrew}
          onPasteCrew={onPasteCrew}
          hasClipboard={hasClipboard}
          hasChanges={hasChanges}
          scale={scale}
        />
      )}
    </div>
  );
}
